import React, { useRef, useEffect, useState, useMemo } from "react";
import { CircularProgress, Box, Typography } from '@mui/material';
import { Row, Col } from "react-bootstrap";
import GraficoPorRiesgo from './CriticidadGrafica';
import AlertaCSV from './AlertaCSV';
import AADService from "../funcionesAuth.js";

// Componente principal
const CriticidadActivos = () => {
    const [datosAPI, setDatosAPI] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [mostrarAlerta, setMostrarAlerta] = useState(false);
    const [idsNoEncontrados, setIdsNoEncontrados] = useState([]);
    const [confi, setConfi] = useState("");
    const [dispo, setDispo] = useState("");
    const [integri, setIntegri] = useState("");
    // Nuevos estados para manejar datos vacíos
    const [sinClasificacion, setSinClasificacion] = useState(false);
    const [sinCriticidad, setSinCriticidad] = useState(false);
    const AzureADService = new AADService();
    const token = AzureADService.getToken();

    const formatearNumero = (value) => {
        const number = parseFloat(value);
        if (isNaN(number)) {
            return value;
        }
        // Formatear con dos decimales
        const formatted = number.toFixed(2);
        // Separar la parte entera de los decimales
        const [integerPart, decimalPart] = formatted.split('.');
        // Agregar puntos como separadores de miles
        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        // Retornar con coma como separador decimal
        return `${formattedInteger}.${decimalPart}`;
    };

    const tabla1 = useMemo(() => {
        if (!datosAPI) return [];
        return [
            {
                nombre: 'Confidencialidad',
                p50: datosAPI['por riesgo']?.['Confidencialidad']?.P50 || 0,
                p95: datosAPI['por riesgo']?.['Confidencialidad']?.P95 || 0,
                p99: datosAPI['por riesgo']?.['Confidencialidad']?.P99 || 0,
            },
            {
                nombre: 'Integridad',
                p50: datosAPI['por riesgo']?.['Integridad']?.P50 || 0,
                p95: datosAPI['por riesgo']?.['Integridad']?.P95 || 0,
                p99: datosAPI['por riesgo']?.['Integridad']?.P99 || 0,
            },
            {
                nombre: 'Disponibilidad',
                p50: datosAPI['por riesgo']?.['Disponibilidad']?.P50 || 0,
                p95: datosAPI['por riesgo']?.['Disponibilidad']?.P95 || 0,
                p99: datosAPI['por riesgo']?.['Disponibilidad']?.P99 || 0,
            },
            {
                nombre: 'Total',
                p50: datosAPI['general']?.[0]?.P50 || 0,
                p95: datosAPI['general']?.[0]?.P95 || 0,
                p99: datosAPI['general']?.[0]?.P99 || 0,
            },
        ];
    }, [datosAPI]);

    // Generar tabla2 dinámicamente desde los datos de la API
    const tabla2 = useMemo(() => {
        if (!datosAPI || !datosAPI.aplicativos) return [];

        return Object.entries(datosAPI.aplicativos).map(([id, aplicativo]) => ({
            id: id,
            app: aplicativo.nombre || `Aplicativo ${id}`,
            confidencialidad: aplicativo['Confidencialidad'] || '—',
            integridad: aplicativo['Integridad'] || '—',
            disponibilidad: aplicativo['Disponibilidad'] || '—'
        }));
    }, [datosAPI]);

    useEffect(() => {
        const obtenerClasi = async () => {
            try {
                const result = await fetch(
                    process.env.REACT_APP_API_URL +
                    "activoClasificacion/" + localStorage.getItem("idactivo") + "/",
                    {
                        method: "GET",
                        headers: {
                            Accept: "application/json",
                            Authorization: "Bearer " + token,
                        },
                    }
                );

                const data = await result.json();

                if (Array.isArray(data) && data.length > 0) {
                    const {
                        clasificacion_confidencialidad,
                        clasificacion_disponibilidad,
                        clasificacion_integridad,
                    } = data[0];

                    // Setea cada valor por separado
                    setConfi(clasificacion_confidencialidad || "");
                    setDispo(clasificacion_disponibilidad || "");
                    setIntegri(clasificacion_integridad || "");
                    setSinClasificacion(false);
                } else {
                    console.warn("No se encontraron clasificaciones.");
                    setConfi("");
                    setDispo("");
                    setIntegri("");
                    setSinClasificacion(true);
                }

            } catch (error) {
                console.error("Error al obtener la clasificación:", error);
                setConfi("");
                setDispo("");
                setIntegri("");
                setSinClasificacion(true);
            }
        };

        const fetchCriticidad = async () => {
            try {
                setLoading(true);
                const controller = new AbortController();
                const result = await fetch(
                    process.env.REACT_APP_API_URL +
                    'evaluacion/CriticidadACI/' +
                    localStorage.getItem('idactivo') +
                    '/',
                    {
                        method: 'GET',
                        headers: {
                            Accept: 'application/json',
                        },
                        signal: controller.signal,
                    }
                );

                const data = await result.json().catch(() => null);

                if (!result.ok) {
                    const backendMessage =
                        data?.message ||
                        data?.error ||
                        data?.detail ||
                        data?.Error ||
                        data?.MESSAGE ||
                        data?.errorMessage ||
                        'Error en: criticidad de activo de informacion';

                    setErrorMessage(backendMessage);
                    setError(result.status);
                    setSinCriticidad(true);
                    return;
                }

                // CAMBIO PRINCIPAL: Verificar si la respuesta contiene una alerta del backend
                if (data && data.alerta) {
                    // El backend envió una alerta (sin contenedores, sin efectos, etc.)
                    setErrorMessage(data.alerta);
                    setError('BACKEND_ALERT');
                    setSinCriticidad(true);
                    setDatosAPI(null);
                    return;
                }

                // Verificar si hay datos de criticidad válidos
                const tieneRiesgo = data?.['por riesgo'] && Object.keys(data['por riesgo']).length > 0;
                const tieneGeneral = data?.['general'] && data['general'].length > 0;
                const tieneAplicativos = data?.['aplicativos'] && Object.keys(data['aplicativos']).length > 0;

                if (!tieneRiesgo && !tieneGeneral && !tieneAplicativos) {
                    setSinCriticidad(true);
                } else {
                    setSinCriticidad(false);
                }

                // Verificar IDs no encontrados en CSV (esta alerta se maneja por separado)
                if (Array.isArray(data?.no_csv) && data.no_csv.length > 0) {
                    setIdsNoEncontrados(data.no_csv);
                    setMostrarAlerta(true);
                }

                setDatosAPI(data);
                setError(null);
                setErrorMessage(null);

            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Error al obtener criticidad:', error);
                    let backendMessage = 'Error de conexión. No se pudieron cargar los datos.';

                    // Para errores de fetch, no hay response del backend
                    setError('CONNECTION_ERROR');
                    setErrorMessage(backendMessage);
                    setSinCriticidad(true);
                }
            } finally {
                setLoading(false);
            }
        };

        obtenerClasi();
        fetchCriticidad();
    }, []);

    const cerrarAlerta = () => {
        setMostrarAlerta(false);
        setIdsNoEncontrados([]);
    };

    const getColorStyle = (valor) => {
        if (!valor) return { backgroundColor: '#f8f9fa', color: '#495057' };

        // Normaliza: minúsculas y sin acentos
        const normalized = valor
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, ""); // elimina las tildes

        switch (normalized) {
            case 'critico':
            case 'muy critico':
            case 'confidencial':
            case 'restringida':
                return { backgroundColor: '#dc3545', color: 'white' };
            case 'moderado':
            case 'interna':
            case 'medio':
                return { backgroundColor: 'orange', color: 'white' };
            case 'tolerable':
            case 'publica':
            case 'bajo':
                return { backgroundColor: '#81C784', color: 'white' };
            default:
                return { backgroundColor: '#f8f9fa', color: '#495057' };
        }
    };


    // Componente para mostrar mensaje del backend
    const MensajeDelBackend = ({ mensaje }) => (
        <div style={{
            backgroundColor: '#CBCCCB',
            color: '#856404',
            padding: '1rem',
            marginBottom: '1rem',
            border: '1px solid #CBCCCB',
            borderRadius: '0.375rem',
            textAlign: 'center'
        }}>
            <Typography variant="h3">
                {mensaje}
            </Typography>
        </div>
    );

    if (loading) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                sx={{ padding: '2rem', minHeight: '200px' }}
            >
                <CircularProgress
                    size={50}
                    thickness={4}
                    sx={{
                        color: '#3498db',
                        marginBottom: '1rem'
                    }}
                />
                <Typography variant="body1" color="text.secondary">
                    Cargando datos de criticidad...
                </Typography>
            </Box>
        );
    }

    return (
        <div style={{ padding: '1rem' }}>
            {/* Alerta para IDs no encontrados en CSV */}
            {mostrarAlerta && idsNoEncontrados.length > 0 && (
                <AlertaCSV idsNoEncontrados={idsNoEncontrados} onClose={cerrarAlerta} />
            )}

            <h2 style={{ marginBottom: '1rem' }}>Criticidad de Activos</h2>
            {/* Solo mostrar la sección de clasificación si hay datos */}
            {!sinClasificacion && (
                <Row className="mt-4">
                    {/* Confidencialidad */}
                    <Col sm={4} xs={12} className="d-flex align-items-center mb-3">
                        <h5 className="mb-0" style={{ marginRight: '1rem', minWidth: '120px', flex: '0 0 auto' }}>
                            Confidencialidad
                        </h5>
                        <input
                            disabled
                            type="text"
                            className="form-control form-control-lg text-center colorAuto"
                            placeholder="Confidencialidad Automático"
                            id="confidencialidad"
                            value={confi}
                            style={{
                                ...getColorStyle(confi),
                                width: '100%',
                                fontSize: '1.1rem',
                                fontWeight: '600',
                                padding: '0.6rem 1rem',
                                borderRadius: '12px'
                            }}
                        />
                    </Col>

                    {/* Disponibilidad */}
                    <Col sm={4} xs={12} className="d-flex align-items-center mb-3">
                        <h5 className="mb-0" style={{ marginRight: '1rem', minWidth: '120px', flex: '0 0 auto' }}>
                            Disponibilidad
                        </h5>
                        <input
                            disabled
                            type="text"
                            className="form-control form-control-lg text-center colorAuto"
                            placeholder="Calculado Automático"
                            id="disponibilidad"
                            value={dispo}
                            style={{
                                ...getColorStyle(dispo),
                                width: '100%',
                                fontSize: '1.1rem',
                                fontWeight: '600',
                                padding: '0.6rem 1rem',
                                borderRadius: '12px'
                            }}
                        />
                    </Col>

                    {/* Integridad */}
                    <Col sm={4} xs={12} className="d-flex align-items-center mb-3">
                        <h5 className="mb-0" style={{ marginRight: '1rem', minWidth: '120px', flex: '0 0 auto' }}>
                            Integridad
                        </h5>
                        <input
                            disabled
                            type="text"
                            className="form-control form-control-lg text-center colorAuto"
                            placeholder="Calculado Automático"
                            id="integridad"
                            value={integri}
                            style={{
                                ...getColorStyle(integri),
                                width: '100%',
                                fontSize: '1.1rem',
                                fontWeight: '600',
                                padding: '0.6rem 1rem',
                                borderRadius: '12px'
                            }}
                        />
                    </Col>
                </Row>
            )}

            {/* Mostrar mensaje de error/alerta cuando corresponda */}
            {errorMessage && (
                <MensajeDelBackend mensaje={errorMessage} />
            )}

            {/* Solo mostrar las tablas y gráficos si hay datos de criticidad */}
            {!sinCriticidad && datosAPI && (
                <>
                    <h4 style={{ marginTop: '0.5rem' }}>Tabla de Características y Percentiles (inherente)</h4>
                    <table
                        style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            marginBottom: '1rem',
                            border: '1px solid #dee2e6',
                        }}
                    >
                        <thead>
                            <tr style={{ backgroundColor: '#212529', color: 'white' }}>
                                <th style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>Característica</th>
                                <th style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>P50 (Esperada)</th>
                                <th style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>P95 (Inesperada)</th>
                                <th style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>P99 (Catastrófica)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tabla1.map((fila, idx) => (
                                <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#f8f9fa' : 'white' }}>
                                    <td
                                        style={{
                                            padding: '0.75rem',
                                            border: '1px solid #dee2e6',
                                            backgroundColor: '#212529',
                                            color: 'white',
                                        }}
                                    >
                                        {fila.nombre}
                                    </td>
                                    <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>
                                        ${formatearNumero(fila.p50)}
                                    </td>
                                    <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>
                                        ${formatearNumero(fila.p95)}
                                    </td>
                                    <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>
                                        ${formatearNumero(fila.p99)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <GraficoPorRiesgo datos={datosAPI} />

                    {tabla2.length > 0 && (
                        <>
                            <h4 style={{ marginTop: '2rem' }}>Tabla de Criticidad y Riesgo (residual)</h4>
                            <table
                                style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    marginBottom: '1rem',
                                    border: '1px solid #dee2e6',
                                }}
                            >
                                <thead>
                                    <tr style={{ backgroundColor: '#212529', color: 'white' }}>
                                        <th style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>ID</th>
                                        <th style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>Aplicativo</th>
                                        <th style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>Confidencialidad</th>
                                        <th style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>Integridad</th>
                                        <th style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>Disponibilidad</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tabla2.map((row, idx) => (
                                        <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#f8f9fa' : 'white' }}>
                                            <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>{row.id}</td>
                                            <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>{row.app}</td>
                                            <td style={{ padding: '0.75rem', border: '1px solid #dee2e6', ...getColorStyle(row.confidencialidad) }}>
                                                {row.confidencialidad}
                                            </td>
                                            <td style={{ padding: '0.75rem', border: '1px solid #dee2e6', ...getColorStyle(row.integridad) }}>
                                                {row.integridad}
                                            </td>
                                            <td style={{ padding: '0.75rem', border: '1px solid #dee2e6', ...getColorStyle(row.disponibilidad) }}>
                                                {row.disponibilidad}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default CriticidadActivos;